import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useCart } from "./CartContext";
import { sendRequest, useHandleApiConflict } from "../utils/api";
import { useModal } from "../context/ConfirmModalContext";
import { useToast } from "../context/ToastContext";
import { errorMessage } from "../utils/api";
import { toBool } from "../utils/helper-function";
import { initAllStores } from "../utils/cache";
import { isOnline } from "../utils/helper-function";
import { clearBill, generatedBillNo, generateReference } from "../utils/bill";
import { syncBillHistory, } from "../utils/sync"
import { OrderHistoryDisplay, CreateOrderBody, ItemToRemove } from "../types/order";
import { BillData } from "../types/bill";
import { v4 as uuidv4 } from "uuid";
import { capitalizeFirst, padNumber } from "../utils/string";
import Hotel from "../models/Hotel";
import User from "../models/User";
import Order from "../models/Order";
import BookedRoom from "../models/BookedRoom";
import Room from "../models/Room";
import { filterNonReservationRooms } from "../utils/booking-helper";
import { orderItemsTotal } from "../utils/orders-helper";
import { ReceiptData } from "../types/print-setting";
import { groupOrderItemsByType } from "../utils/sales-summary";
import { terminal } from "../utils/user-helper";
import { formatDateForInput, formatDateTime } from "../utils/date";
import { useLiveOrders } from "./LiveOrdersContext";

interface OrderContextType {
  identifier: string;
  setIdentifier: (val: string) => void;
  orderType: string;
  setOrderType: (val: OrderType) => void;
  isPaid: string;
  setIsPaid: (val: string) => void;
  paymentMethod: string;
  setPaymentMethod: (val: PaymentType) => void;
  discount: string;
  setDiscount: (val: string) => void;
  description: string;
  setDescription: (val: string) => void;
  placeholder: string;
  setPlaceholder: (val: string) => void;
  loading: boolean;
  showOrderConfirmPopUp: () => void;
  // syncOrder: () => Promise<OrderHistoryDisplay[]>;
  loadOrderHistory: () => void;
  showClearOrderConfirmPopUp: (
    orderId: string, setOrderHistory: React.Dispatch<React.SetStateAction<OrderHistoryDisplay[]>>,
  ) => Promise<void>;
  showBill: boolean;
  setShowBill: (val: boolean) => void;
  orderBillData: any;
  loadOrderBill: (orderId: string) => Promise<boolean>;
  showRemoveOrderModal: (
    orderId: string,
    setShowRemoveModal: React.Dispatch<React.SetStateAction<boolean>>,
    setOrderItems: React.Dispatch<React.SetStateAction<ItemToRemove[]>>,
  ) => Promise<void>;
  deletSelectedItems: (
    itemId: string[],
    // setOrderHistory: React.Dispatch<React.SetStateAction<OrderHistoryDisplay[]>>,
    orderId: string,
  ) => Promise<void>;
  orderHistory: OrderHistoryDisplay[];
  setOrderHistory: React.Dispatch<React.SetStateAction<OrderHistoryDisplay[]>>;
  ordersByDate(start: string, end: string): void;

  receiptData: ReceiptData;
  showReceiptModal: boolean;
  setShowReceiptModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

// Sample receipt data
const SAMPLE_RECEIPT: ReceiptData = {
  guest: "John Adewale",
  items: [
    { desc: "Room (Standard) - 2 nights", amount: "₦40,000" },
    { desc: "Breakfast Buffet (2)", amount: "₦5,000" },
    { desc: "Laundry Service", amount: "₦3,000" },
  ],
  total: "₦51,600",
  vat: "₦3,600",
  paymentMethod: "POS",
  reference: "202511041231-POS-TRX",
  address: "15 Unity Road, Lagos",
  phone: "+234 812 345 6789",
  email: "test@wiventory.com",
  website: "www.wiventory.com",
  issuedBy: "Wisdom Okposin",
  discount: "₦2,000",
};


export const OrderProvider = ({ children }: { children: ReactNode }) => {

  const { cart } = useCart();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const { handleApiConflict } = useHandleApiConflict({
    onReload: () => loadOrderHistory()
  });
  // const { orders } = useLiveOrders();

  const [terminalId] = useState(() => terminal());

  const [orderHistory, setOrderHistory] = useState<OrderHistoryDisplay[]>([]);

  // Odrder Info
  const [identifier, setIdentifier] = useState("");
  const [orderType, setOrderType] = useState<OrderType>("");
  const [discount, setDiscount] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPaid, setIsPaid] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentType>("");
  const [placeholder, setPlaceholder] = useState("");

  const [showBill, setShowBill] = useState(false);
  const [orderBillData, setOrderBillData] = useState<BillData>({} as BillData);

  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState<ReceiptData>(SAMPLE_RECEIPT);

  const orderIdentifier = {
    new: { customerName: identifier || "Not Provided" },
    existing: { orderNumber: identifier },
    room: { bookedRoomId: identifier }
  };

  useEffect(() => {
    setIdentifier("");
    if (orderType === "new") {
      setPlaceholder("Guest Name (Optional)");
    } else if (orderType === "existing") {
      setPlaceholder("Order No.");
    } else if (orderType === "room") {
      setPlaceholder("Room No.");
    }
  }, [orderType]);
  // Total
  const total =
    cart.reduce((sum, item) => sum + item.amount * item.quantity, 0);

  const showOrderConfirmPopUp = async () => {
    const cache = await initAllStores();

    const missingFields: string[] = [];
    //if (!guestName) missingFields.push("Guest Name");
    if (!orderType) missingFields.push("Order Type");
    if (!isPaid && orderType !== "existing") missingFields.push("Payment Instance");
    if (!paymentMethod && orderType !== "existing") missingFields.push("Payment Method");

    if (!identifier && orderType === "existing") missingFields.push("Order Number");
    else if (!identifier && orderType === "room") missingFields.push("Room Number");

    if (missingFields.length > 0) {
      return openModal(
        "Missing Fields",
        `Please fill in: ${missingFields.join(", ")}`,
        closeModal,
        false,
        "Close"
      );
    }

    if (cart.length === 0) {
      return openModal("Empty Cart", "Your cart is empty.", closeModal, false);
    }

    if (orderType === "room") {
      const room: Room | null = await cache.getBy("rooms", { number: identifier.toLocaleUpperCase() });
      if (!room) {
        setLoading(false);
        return openModal(
          "Not Found",
          `Room "${identifier}" was not found. Please check and try again.`,
          closeModal,
          false,
          "Close"
        );
      } else if (!room.isOccupied) {
        setLoading(false);
        return openModal(
          "Room Not Occupied",
          `Room "${identifier}" is not linked to any active stay at the moment. Please select an occupied room to place an order.`,
          closeModal,
          false,
          "Close"
        );
      }
      
      orderIdentifier[orderType]["bookedRoomId"] = room.id;
    }

    // Check if stock is sufficient for order.
    for (const cartItem of cart) {
      const item = await cache.getBy(cartItem.itemType, { id: cartItem.id });

      // ✅ Check if item exists
      if (!item) {
        return openModal(
          "Item Not Found",
          "One or more items in your cart could not be found in the system. Please refresh and try again.",
          closeModal,
          false,
          "Close"
        );
      }

      // ✅ Check stock availability
      if (item.quantity < cartItem.quantity) {
        return openModal(
          "Insufficient Stock",
          `The available stock for "${item.name}" is ${item.quantity}, which is less than your requested quantity of ${cartItem.quantity}. Please adjust your order before proceeding.`,
          closeModal,
          false,
          "Close"
        );
      } else if (!item.isAvailable && cartItem.itemType !== "drinks" && cartItem.itemType !== "foods") {
        return openModal(
          "Unavailable Item",
          `"${item.name}" has been marked as unavailable. Please update availability or select a different item.`,
          closeModal,
          false,
          "Close"
        );
      }
    }

    openModal("Confirm Order", "Confirm placing this order?", () =>
      handleConfirmOrder()
    );
  };

  const syncOrder = async (date?: { start: string, end: string }): Promise<OrderHistoryDisplay[] | []> => {
    const cache = await initAllStores();
    let allTodayHistory: OrderHistoryDisplay[] = [];
    try {
      setLoading(true);
      const unsyncData = await cache.getAllBy("orders", { isSynced: false });
      const { orders } = await syncBillHistory({ orders: unsyncData }, date);
      if (!orders) return allTodayHistory;

      const mappedOrders = await Promise.all(
        orders.map(async (order: any) => {
          const enrichOrders = {
            id: order.id,
            createdAt: formatDateTime(order.createdAt),
            customerName: order.customer?.name ?? order.guestName ?? "Deleted Guest",
            room: order?.roomNumber?.toUpperCase() ?? "N/A",
            // room: "102",
            orderNo: padNumber(order?.orderNo ?? 0, 4),
            paymentMethod: capitalizeFirst(order.paymentMethod) as "Cash" | "POS" | "Transfer",
            paymentStatus: order.isPaid ? "Paid" : ("Pending" as "Paid" | "Pending"),
            discount: order.discount,
            amount: orderItemsTotal(order?.orderItems).toLocaleString(),
          };
          return enrichOrders;
        })
      );

      allTodayHistory = mappedOrders.sort((a, b) => Number(b.orderNo) - Number(a.orderNo));
    } catch (err: any) {
    } finally {
      setLoading(false);
    }
    return allTodayHistory;
  };

  const loadOrderHistory = async () => {
    const now = formatDateForInput(new Date());
    const allTodayHistory: OrderHistoryDisplay[] = await syncOrder();
    setOrderHistory(allTodayHistory);
  };

  const handleConfirmOrder = async (forceOrder: boolean = false) => {
    const cache = await initAllStores();
    closeModal();
    setLoading(true);

    // const name = guestName ? guestName : "Not Provided";
    const user = await cache.getBy("data", { id: "user" }) ?? {};
    // const hotel = await cache.getBy("data", { id: "hotel" }) ?? {};

    const refNo = generateReference(
      paymentMethod.toLocaleLowerCase() as PaymentType, "order"
    );
    if (!orderType) return;

    const now = new Date();
    const orderData: CreateOrderBody = {
      identifier: orderIdentifier[orderType],
      bill: {
        id: uuidv4(),
        orderedById: user.userId,
      },

      order: {
        createdAt: now,
        updatedAt: now,
        id: uuidv4(), refNo,
        isPaid: toBool(isPaid as "true" | "false"),
        paymentMethod,
        terminal: terminalId,
        orderedById: user.userId,
        clearedById:
          toBool(isPaid as "true" | "false") ? user.userId : null,
        discount: parseFloat(discount || "0"),
        //hotelId: hotel.hotelId,
        description,
        amount: total,
        forceOrder,
      },
      items: cart.map((item) => ({
        createdAt: now,
        updatedAt: now,
        id: item.id,
        itemName: item.itemName,
        amount: item.amount * item.quantity,
        quantity: item.quantity,
        // category: item.category,
        itemType: item.itemType,
        itemId: item.id,
        terminal: terminalId
      })),
    };

    try {

      // Offline method not available now.
      if (!isOnline()) {
        const err = errorMessage(408);
        if (err) openModal(err.title, err.msg, closeModal, false, "Close");
        return;
      }

      let isSynced: boolean = false;;
      if (isOnline()) {
        await sendRequest("POST", "/orders", orderData);
        //clearCart();
        isSynced = true;
      }

      // Update stock.
      for (const cartItem of cart) {
        const item = await cache.getBy(cartItem.itemType, { id: cartItem.id });

        if (item) {
          const newQuantity = Math.max(item.quantity - cartItem?.quantity, 0); // avoid negatives

          await cache.updateManyBy(cartItem.itemType, [cartItem.id], {
            ...item,
            quantity: newQuantity,
          });
        }
      }

      //  Make offlline order.
      let identifier: any = {};

      if (orderType === "room") {
        const roomId = orderIdentifier[orderType]["bookedRoomId"];

        const room: Room | null = await cache.getBy("rooms", { id: roomId });
        if (!room) throw Error("Room not found.");
        // const bookedRooms: BookedRoom[] | undefined = bookedRooms;
        const bookedRooms: BookedRoom[] = await filterNonReservationRooms(
          await cache.getAllBy("bookedRooms", { roomId }), cache
        );

        if ((bookedRooms ?? []).length === 0) {
          setLoading(false);
          return openModal(
            "No Active Booking Found",
            `The room "${room.number}" has no active bookings at the moment. If this issue persists, please log out and log in again.`,
            closeModal,
            false,
            "Close"
          );
        }
        identifier = {
          room: { number: room.number },
          bookedRoomId: bookedRooms[0].id,
          guestName: bookedRooms[0].guestName
        };
      } else {
        identifier = { ...orderData.identifier, };
      }
      const data = {
        ...orderData.order,
        ...(orderType === "new")
          ? { customer: { name: identifier.customerName } }
          : { bookedRoom: { ...identifier } },
        ...identifier,
        isSynced,
        bill: orderData.bill,
        orderItems: orderData.items,
        orderedBy: { firstName: user.firstName, lastName: user.lastName }
      };

      await cache.saveOne("orders", data);

      openModal(
        "Order Confirmed",
        "The order has been successfully processed and confirmed.",
        async () => {
          await loadOrderBill(data.id);
        },
        true,
        orderData.order.isPaid ? "View Receipt" : "View Bill",
        "Close"
      );

      // await cache.saveOne("orders", data);
      setDiscount("0");

    } catch (error: any) {
      console.log(error)
      const err = errorMessage(error.status);
      if (error.status === 409) {
        if (error.status === 409) {
          openModal(
            "Possible Duplicate Order",
            "This looks like a duplicate order for the same guest. Do you still want to proceed?",
            async () => {
              await handleConfirmOrder(true);
              // closeModal();
            },
            true, "Proceed", "Cancel",
          );
        }

      } else if (error.status === 404 && orderType === "existing") {
        openModal(
          "Order Not Found",
          `No order was found with the order number "${identifier}".`,
          closeModal,
          false,
          "Close"
        );
      } else if (error.status === 422) {
        openModal(
          "Order Closed",
          // `The order with number "${identifier}" has already been marked as complete since its bill has been cleared. You cannot add items to this order. Please create a new order instead.`,
          error.response.data.error,
          closeModal,
          false,
          "Close"
        );
      } else if (err) {
        openModal(err.title, err.msg, closeModal, false, "Close");
      }
    } finally {
      setLoading(false);
    }
  };

  const showClearOrderConfirmPopUp = async (
    orderId: string,
    // setOrderHistory: React.Dispatch<React.SetStateAction<OrderHistoryDisplay[]>>,
  ) => {
    openModal(
      "Clear Order Bill 💳",
      "Are you sure you want to clear this bill? This action cannot be undone!",
      () => handleClearingBill(orderId),
      true,
      "Yes, Clear",
      "Cancel"
    );
  };

  const showRemoveOrderModal = async (
    orderId: string,
    setShowRemoveModal: React.Dispatch<React.SetStateAction<boolean>>,
    setOrderItems: React.Dispatch<React.SetStateAction<ItemToRemove[]>>,
  ): Promise<void> => {

    const cache = await initAllStores();
    const order: Order | null = await cache.getBy("orders", { id: orderId });
    // const orderItems = await enrichOrderItems(order?.orderItems ?? [], { isOrder: true });
    const orderItems = order?.orderItems;
    setOrderItems(orderItems as ItemToRemove[]);
    setShowRemoveModal(true);
  };

  const deletSelectedItems = async (
    itemIds: string[],
    // setOrderHistory: React.Dispatch<React.SetStateAction<OrderHistoryDisplay[]>>,
    orderId: string
  ) => {
    try {
      const cache = await initAllStores();
      setLoading(true);

      await sendRequest("DELETE", `/orders/${orderId}/items`, { itemIds });

      const order: Order | null = await cache.getBy("orders", { id: orderId });
      if (!order) throw new Error("Order not found");

      // Remove selected items from the order's items array
      const filteredItems = (order.orderItems ?? []).filter(
        (item) => !itemIds.includes(item.id)
      );

      const deletedItems = (order.orderItems ?? []).filter((item) =>
        itemIds.includes(item.id)
      );

      order.orderItems = filteredItems;

      const newTotalAmount = filteredItems.reduce((sum, item) => {
        return sum + (item.amount * item.quantity)
      }, 0);

      await cache.updateManyBy("orders", [orderId], { ...order });

      if (order.orderItems.length === 0) {
        await cache.deleteOne("orders", orderId);
        setOrderHistory((prev) =>
          prev.filter((order) => order.id !== orderId)
        );
      } else {
        setOrderHistory((prev) =>
          prev.map((order) =>
            order.id === orderId
              ? { ...order, amount: newTotalAmount.toLocaleString() }
              : order
          )
        );
      }

      setOrderHistory((prev) =>
        prev.map((order) =>
          order.id === orderId
            ? { ...order, amount: newTotalAmount.toLocaleString() }
            : order
        )
      );

      showToast("Items Remove Successfully", { type: "success" });

      for (const removedItem of deletedItems) {
        const store = removedItem.itemType;
        if (!store) continue;

        // console.log(store);

        const stockItem = await cache.getBy(store, { id: removedItem.itemId });
        if (stockItem) {
          // console.log({ itemId: removedItem.itemId, qty: stockItem.quantity, qtt: removedItem.quantity });
          await cache.updateManyBy(store, [removedItem.itemId ?? ""], {
            quantity: stockItem.quantity + removedItem.quantity,
          });
        }
      }

    } catch (error) {

      const handled = handleApiConflict(error, "order");
      if (!handled) {
        showToast(
          "Failed to remove items. Please try again.", { type: "error" }
        );
      }
      // onClose();

    } finally {
      setLoading(false);
    }
  };

  const handleClearingBill = async (
    orderId: string,
    // setOrderHistory: React.Dispatch<React.SetStateAction<OrderHistoryDisplay[]>>,
  ): Promise<void> => {
    try {
      setLoading(true);

      // Call backend / IndexedDB clear
      await clearBill("order", orderId);

      // Update UI state immediately
      setOrderHistory(prev =>
        prev.map(order =>
          order.id === orderId ? { ...order, paymentStatus: "Paid" } : order
        )
      );

      // Success modal
      showToast("Bill Clear Successfully", { type: "success" });

      // return true;
    } catch (error: any) {
      // const err = errorMessage(error.status);
      const handled = handleApiConflict(error, "order");
      if (!handled) {
        showToast(
          "Failed to remove items. Please try again.", { type: "error" }
        );
      }
      // return false;
    } finally {
      setLoading(false);
    }
  };

  const loadOrderBill = async (orderId: string): Promise<boolean> => {
    const cache = await initAllStores();

    const hotel: Hotel | null = await cache.getBy("data", { id: "hotel" });
    const user: User | null = await cache.getBy("data", { id: "user" });
    const order: Order | null = await cache.getBy("orders", { id: orderId });
    if (!hotel || !user || !order) {
      // TODO: Suggest user to logout and login again.
      return false
    }

    const bookedRoom = await cache.getBy("bookedRooms", { id: order.bookedRoomId });
    // const orderItems = await enrichOrderItems(order?.orderItems ?? []);

    const guestName = order?.customer?.name ?? bookedRoom?.guestName;
    // order["orderItems"] = orderItems;

    if (order.isPaid) {
      const items = (order?.orderItems ?? []).map(od => ({
        amount: od.amount.toLocaleString(),
        desc: `${od.quantity} x ${od.itemName}`,
      }));
      const data = {
        // receiptNo: string;
        guest: guestName ?? "Delete Customer", items,
        total: orderItemsTotal(order?.orderItems).toLocaleString(),
        // vat: vatCharge.toLocaleString(),
        // discount: booking.discount?.toLocaleString(),
        // paymentMethod, discount: discount,
        reference: order.refNo,
        address: hotel.address,
        // serviceCharge,
        phone: hotel.phone,
        email: hotel.email,
        website: hotel.website,
        issuedBy: user.firstName + " " + user.lastName,
      };
      setReceiptData(data);
      setShowReceiptModal(true);
    } else {
      const billNumber = generatedBillNo("BL", order?.bill?.billNo);

      setOrderBillData({
        issuedBy: user.firstName + " " + user.lastName,
        orders: [order],
        discount: order.discount,
        hotel, guestName, billNumber
      })
      setShowBill(true);
    }
    return true;
    // setOrderBillData();
  };

  const ordersByDate = async (start: string, end: string) => {
    const allTodayHistory: OrderHistoryDisplay[] = await syncOrder({ start, end });
    setOrderHistory(allTodayHistory);
  }

  return (
    <OrderContext.Provider
      value={{
        identifier,
        setIdentifier,
        orderType,
        setOrderType,
        isPaid,
        setIsPaid,
        paymentMethod,
        setPaymentMethod,
        discount,
        setDiscount,
        description,
        setDescription,
        placeholder,
        setPlaceholder,
        loading,
        showOrderConfirmPopUp,
        // syncOrder,
        loadOrderHistory,
        showClearOrderConfirmPopUp,
        showBill,
        setShowBill,
        orderBillData,
        loadOrderBill,
        showRemoveOrderModal,
        deletSelectedItems,
        orderHistory,
        setOrderHistory,
        ordersByDate,
        receiptData,
        showReceiptModal,
        setShowReceiptModal,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) throw new Error("useOrder must be used inside OrderProvider");
  return context;
};